import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Star, ArrowRight, Gift, TrendingUp, Zap } from 'lucide-react';

const Reviews = () => {
  const reviews = [
    {
      id: 1,
      slug: '32red-review',
      name: '32Red',
      url: 'https://www.32red.com',
      bonus: 'Get 150% up to £150 on your first deposit + 25 free spins',
      icon: <Gift className="w-8 h-8" />,
      gradient: 'from-brown-600 to-brown-800',
      rating: 4.5,
      highlight: 'Trusted operator with nearly two decades of UK market presence'
    },
    {
      id: 2,
      slug: 'highbet-review',
      name: 'Highbet',
      url: 'https://www.highbet.co.uk/',
      bonus: 'Get 50 Free Spins on big bass splash',
      icon: <TrendingUp className="w-8 h-8" />,
      gradient: 'from-beige-400 to-brown-600',
      rating: 4.3,
      highlight: 'Modern platform with impressive game selection and fast payouts'
    },
    {
      id: 3,
      slug: 'playojo-review',
      name: 'PlayOJO',
      url: 'https://www.playojo.com',
      bonus: 'Get 50 Free Spins on 1st deposit',
      icon: <Zap className="w-8 h-8" />,
      gradient: 'from-brown-700 to-beige-600',
      rating: 4.7,
      highlight: 'Revolutionary no-wagering approach sets new industry standard'
    }
  ];

  return (
    <div className="min-h-screen bg-beige-50">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brown-900 to-brown-950 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center space-x-2 mb-6 px-4 py-2 glass-dark rounded-full">
                <Star className="w-5 h-5" />
                <span className="font-medium">In-Depth Casino Reviews</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                UK Casino Reviews & Ratings
              </h1>
              <p className="text-xl text-beige-200 max-w-2xl mx-auto leading-relaxed">
                Comprehensive, honest reviews of the UK's top online casinos. We test every aspect so you can make informed decisions.
              </p>
            </div>
          </div>
        </section>

        {/* Reviews Grid */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {reviews.map((review) => (
                <Link
                  key={review.id}
                  to={`/reviews/${review.slug}`}
                  data-discover="true"
                  className="block glass-strong rounded-3xl overflow-hidden hover:scale-105 transition-transform duration-300 group"
                >
                  {/* Review Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={`/images/${review.id === 1 ? 'amanda-jones-K2PAVcngNvY-unsplash.webp' : review.id === 2 ? 'esteban-lopez-OfdFHy1zxjQ-unsplash.webp' : 'terry-vlisidis-vPQbo1D7Eco-unsplash.webp'}`}
                      alt={review.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-beige-900/40 to-brown-900/40"></div>
                    <div className={`absolute top-4 left-4 w-16 h-16 rounded-2xl bg-gradient-to-br ${review.gradient} flex items-center justify-center text-white`}>
                      {review.icon}
                    </div>
                    <div className="absolute bottom-4 right-4 px-3 py-1 glass rounded-full text-xs font-semibold text-white">
                      {review.rating} / 5
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-brown-900 mb-3 group-hover:text-brown-700 transition-colors">
                      {review.name}
                    </h3>
                    <p className="text-brown-700 mb-4 leading-relaxed">
                      {review.highlight}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 ${i < Math.floor(review.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-brown-300'}`}
                          />
                        ))}
                        <span className="ml-2 text-brown-700 font-semibold">{review.rating}</span>
                      </div>
                    </div>
                    <p className="text-brown-600 text-sm mb-4">{review.bonus}</p>
                    <div className="flex items-center space-x-2 text-brown-800 font-semibold group-hover:text-brown-900 transition-colors">
                      <span>Read Full Review</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Review Methodology */}
            <div className="glass-strong rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-brown-900 mb-6">
                Our Review Process
              </h2>
              <div className="space-y-6 text-lg text-brown-700 leading-relaxed">
                <p>
                  Every casino review begins with comprehensive testing across all platform aspects that 
                  matter to UK players. We deposit real funds, test payment processing, evaluate game 
                  quality, and assess customer support responsiveness. This hands-on approach ensures our 
                  reviews reflect actual player experiences rather than theoretical analysis based on 
                  marketing materials or operator claims.
                </p>
                <p>
                  Our testing methodology covers security and licensing verification, game selection 
                  diversity and quality, bonus terms transparency, withdrawal speed and reliability, 
                  customer support effectiveness, mobile platform performance, and responsible gambling 
                  tools availability. Each category receives detailed evaluation that translates into 
                  practical insights you can use when choosing where to play.
                </p>
                <p>
                  Ratings reflect real-world performance rather than marketing budgets or promotional 
                  relationships. We don't accept payment for positive reviews, and our editorial 
                  independence ensures that ratings accurately represent casino quality regardless of 
                  commercial considerations. This integrity means you can trust our assessments to guide 
                  your casino selection decisions with confidence.
                </p>
                <p>
                  We update reviews regularly as casinos evolve, ensuring that our ratings reflect current 
                  platform quality rather than outdated impressions. Bonus terms change, new games launch, 
                  payment methods evolve, and customer service standards shift—our reviews stay current 
                  with these developments so you always have access to accurate, up-to-date information 
                  when making casino choices.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Reviews;

